import type { ComponentProps } from "react";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { useLogout } from "@/app/hooks";
import { ROUTES } from "@/app/router";
import { useCancelSubscriptionContext } from "@/features/billing/context";
import {
  useActiveSubscriptions,
  useCancellationDiscount,
} from "@/features/billing/hooks";
import { formatDate } from "@/shared/lib";
import { Button, Dialog, HeartBrokenIcon } from "@/shared/ui";

export const CancellationFarewellModal = (
  props: ComponentProps<typeof Dialog.Root>,
) => {
  const { data: activeSubscriptions } = useActiveSubscriptions();

  const { subscription } = useCancelSubscriptionContext();

  const { data: discount, isSuccess: isSuccessDiscount } =
    useCancellationDiscount({
      subscriptionId: subscription.id,
    });

  const navigate = useNavigate();

  const logoutMutation = useLogout();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleBackToHome = () => {
    navigate(ROUTES.HOME);
  };

  if (!isSuccessDiscount) {
    return null;
  }

  const endDate =
    discount.endDate && formatDate(discount.endDate, "MMM dd, yyyy");

  const isCancelingLastSubscription = activeSubscriptions?.length === 1;

  return (
    <Dialog.Root {...props}>
      <Dialog.Content>
        <Dialog.Header icon={HeartBrokenIcon}>
          <Dialog.Title>
            {isCancelingLastSubscription
              ? t`We'll miss you at LevelsRx!`
              : t`Subscription canceled`}
          </Dialog.Title>
          <Dialog.Description>
            {isCancelingLastSubscription
              ? t`Your LevelsRx subscription will change on ${endDate}. You can continue using the platform until then and reactivate your subscription anytime from the Billing tab.`
              : t`Your ${subscription.currentProduct.name} subscription will end on ${endDate}. Don't worry—you still have other active subscriptions, so you'll continue enjoying LevelsRx without interruption.`}
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer>
          {isCancelingLastSubscription && (
            <Button size="lg" variant="secondary" onClick={handleLogout}>
              {t`Log out`}
            </Button>
          )}

          <Button size="lg" onClick={handleBackToHome}>
            {t`Back to Home`}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
